import patchAccountableBankingOnboarding, {
  type AccountableOnboardingBody,
  type AccountableOnboardingResponse,
} from 'api/v2/transactions/patchAccountableBankingOnboarding';
import getOngoingOnboarding from 'api/v2/transactions/getOngoingOnboarding';
import { getStoredAuthData } from 'utils/auth';
import useQuery from 'hooks/react-query/useQuery';
import useMutation from 'hooks/react-query/useMutation';
import useQueryClient from 'hooks/react-query/useClientQuery';
import { BankTransactionsCacheKeys } from 'types/cacheKeys.types';
import type { SwanOnboardingStepIdType } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboardingStepIds';
import type { ISwanOnboarding } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';

/**
 * Shared cache key — same as useGetBankingOnboardingStatus.
 * Both hooks read from the same cache entry; this hook uses `select`
 * to derive a mapped shape without duplicating the HTTP request.
 */
export const ONBOARDING_STATUS_QUERY_KEY =
  BankTransactionsCacheKeys.OngoingOnboarding;

export interface OnboardingStatusResponse {
  hasOngoingOnboarding: boolean;
  furthestStepCompleted: SwanOnboardingStepIdType | null;
  onboardingId: string | null;
  /** Raw onboarding data for hydrating form state on resume. */
  onboarding?: ISwanOnboarding;
}

/**
 * Hook to fetch the current onboarding status.
 *
 * Uses the same cache key and queryFn as useGetBankingOnboardingStatus,
 * but transforms the raw API response via react-query's `select` option.
 * This guarantees a single HTTP request and consistent cache state
 * across all consumers of the onboarding endpoint.
 */
export const useGetOnboardingStatus = () => {
  const accessToken = getStoredAuthData('access_token');
  const isAuthenticated = !!accessToken;

  return useQuery([ONBOARDING_STATUS_QUERY_KEY], getOngoingOnboarding, {
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
    select: (data): OnboardingStatusResponse => ({
      hasOngoingOnboarding: data.hasOngoingOnboarding,
      furthestStepCompleted:
        (data.onboarding?.furthestStepCompleted as SwanOnboardingStepIdType) ??
        null,
      onboardingId: data.onboarding?._id ?? null,
      onboarding: data.onboarding,
    }),
  });
};

/**
 * Hook to update onboarding data (including furthestStepCompleted).
 * Invalidates the shared onboarding cache so both useGetBankingOnboardingStatus
 * and useGetOnboardingStatus pick up the new data.
 */
export const useUpdateOnboardingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AccountableOnboardingResponse,
    Error,
    AccountableOnboardingBody
  >((body) => patchAccountableBankingOnboarding(body), {
    onSuccess: () => {
      queryClient.invalidateQueries(ONBOARDING_STATUS_QUERY_KEY);
    },
  });
};
